'use client';

import { RealtyFilterParamsSync, RealtyStoreProvider } from '../../stores/realty-store';
import { RealtyFilterLeft } from './filter/realty-filter-left';
import { RealtiesList } from './realties-list';
import { useSearchParams } from 'next/navigation';
import { useQuery } from '@tanstack/react-query';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { fetchRealtyTypes } from '../../api/realty/realty-api';
import { RealtyFilterTop } from './filter/realty-filter-top';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import {
  constrainRealtyFilterCapabilities,
  parseRealtySearchParams,
} from '../../utils/realty-filter-contract';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import styles from './realty-listing.module.css';
import type { UnderConstructionSearchCapability } from './filter/realty-filter-recipe';
import type { RealtyListingSeed } from '../../utils/realty-listing-seed';

type RealtyPageContentProps = {
  initialListing?: RealtyListingSeed;
  underConstructionSearchCapability?: UnderConstructionSearchCapability;
};

export const RealtyPageContent = ({
  initialListing,
  underConstructionSearchCapability = 'unavailable',
}: RealtyPageContentProps) => {
  const locale = useLocaleStore(s => s.currentLocale);
  const { t } = useTranslations();
  const searchParams = useSearchParams();
  const supportsUnderConstruction = underConstructionSearchCapability === 'indexed';
  const appliedFilter = constrainRealtyFilterCapabilities(parseRealtySearchParams(searchParams), {
    underConstruction: supportsUnderConstruction,
  });
  const filterButtonEnabled = useGlobalStore(
    state => state.templateSettings?.realty_category?.filter_button_in_filter?.enabled === true,
  );
  const listingFilterEnabled = useGlobalStore(
    state => state.templateSettings?.realty_category?.left_filter?.enabled !== false,
  );

  const { data: realtyTypes } = useQuery({
    queryKey: [CacheKey.REALTY_TYPES, locale],
    queryFn: () => fetchRealtyTypes({ locale }),
    initialData: initialListing?.locale === locale ? initialListing.types : undefined,
    initialDataUpdatedAt: initialListing?.fetchedAt,
  });

  return (
    <div className={styles.pageContent} data-eli-part="listing-content">
      <RealtyStoreProvider
        state={{
          autoUpdateSearchParams: !filterButtonEnabled,
          filter: appliedFilter,
          appliedFilter,
          hasHydratedSearchParams: true,
          supportsUnderConstruction,
        }}
      >
        <RealtyFilterParamsSync />
        <h1 className={styles.heading} data-eli-part="listing-heading">
          {realtyTypes?.data.find(type => type.id.toString() === appliedFilter.type)?.name ||
            t('text3430')}
        </h1>
        <div
          className={styles.mobileToolbar}
          data-eli-part="listing-mobile-toolbar"
          data-filter-enabled={listingFilterEnabled ? 'true' : 'false'}
        >
          <RealtyFilterTop
            filterEnabled={listingFilterEnabled}
            underConstructionSearchCapability={underConstructionSearchCapability}
          />
        </div>
        <div className={styles.layout} data-eli-part="listing-layout">
          {listingFilterEnabled && (
            <RealtyFilterLeft
              underConstructionSearchCapability={underConstructionSearchCapability}
            />
          )}
          <RealtiesList
            initialListing={initialListing}
            supportsUnderConstruction={supportsUnderConstruction}
          />
        </div>
      </RealtyStoreProvider>
    </div>
  );
};
